import {
  IOptionsStorage,
  ISortByField,
  TFilterGroup,
} from "@tedivo/tedivo-smart-table";

import { ISavedFilter } from "@tedivo/tvd-api-models";
import Services from "../services";
import securityModule from "../security/SecurityModule";

const storage =
  window.sessionStorage ||
  new (class sessStorate {
    private cache = new Map<string, string>();
    getItem = (key: string) => this.cache.get(key);
    setItem = (key: string, value: string) => this.cache.set(key, value);
    removeItem = (key: string) => this.cache.delete(key);
    clear = () => this.cache.clear();
    length = () => this.cache.size;
    key = (index: number) => {
      const keys = Array.from(this.cache.keys());
      return keys[index];
    };
  })();

class TableUserStorageInstance<T> implements IOptionsStorage<T, ISavedFilter> {
  constructor(public readonly storageKey: string) {}

  storeFilterText = (filterText: string) => {
    storage.setItem(`${this.storageKey}-filterText`, filterText);
  };

  retrieveFilterText = () => {
    const filterText = storage.getItem(`${this.storageKey}-filterText`);
    return filterText || "";
  };

  storeFilterTree = async (
    filterTree: TFilterGroup<T>,
    id: string | undefined,
    name: string | undefined,
    scope: string | undefined,
  ) => {
    if (id || name) {
      const newSF = await Services.savedFilters.update({
        id,
        name,
        scope,
        filterDefinition: filterTree,
        organizationId: securityModule.currentOrganizationId,
      } as ISavedFilter);

      const newId = newSF.data?.id;

      storage.setItem(
        `${this.storageKey}-filterTree`,
        JSON.stringify(filterTree),
      );

      return newId || "";
    } else {
      storage.setItem(
        `${this.storageKey}-filterTree`,
        JSON.stringify(filterTree),
      );
      return "";
    }
  };

  removeFilterTree = async (id: string) => {
    await Services.savedFilters.delete(id);
  };

  retrieveFilterTree = (id: string | undefined) => {
    const filterTree = id
      ? storage.getItem(`${this.storageKey}-filterTree-${id}`)
      : storage.getItem(`${this.storageKey}-filterTree`);

    return filterTree ? JSON.parse(filterTree) : undefined;
  };

  fetchAllFilterTrees = async () => {
    const savedFilters = await Services.savedFilters.getAll(this.storageKey);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filterGroups = (savedFilters.data as any)?.items || [];

    storage.setItem(
      `${this.storageKey}-savedFilters`,
      JSON.stringify(filterGroups),
    );

    return filterGroups;
  };

  storeSortFields = (sortFields: Array<ISortByField<T>>) => {
    storage.setItem(
      `${this.storageKey}-sortFields`,
      JSON.stringify(sortFields),
    );
  };

  retrieveSortFields = () => {
    const sortFields = storage.getItem(`${this.storageKey}-sortFields`);
    return sortFields
      ? (JSON.parse(sortFields) as Array<ISortByField<T>>)
      : undefined;
  };
}

export function createTableUserStorageInstance<T>(
  storageKey: string,
): TableUserStorageInstance<T> {
  return new TableUserStorageInstance<T>(storageKey);
}
